(() => {
  const SECTION_SELECTOR = '[data-drift-gallery-section]';
  const MOBILE_QUERY = '(max-width: 768px)';

  function initGallery(section) {
    if (!section || section.dataset.driftGalleryInitialized === 'true') return;

    const track = section.querySelector('[data-drift-gallery]');
    const prevButton = section.querySelector('[data-drift-gallery-prev]');
    const nextButton = section.querySelector('[data-drift-gallery-next]');
    const dotsWrap = section.querySelector('[data-drift-gallery-dots]');

    if (!track || !prevButton || !nextButton || !dotsWrap) return;

    section.dataset.driftGalleryInitialized = 'true';

    const mobile = window.matchMedia(MOBILE_QUERY);

    let slides = [];
    let dots = [];
    let activeIndex = 0;
    let scrollRaf = 0;
    let resizeRaf = 0;
    let lastMobileState = mobile.matches;

    function getSlides() {
      slides = Array.from(
        track.querySelectorAll('[data-drift-gallery-slide]')
      );
    }

    function restoreDesktopOrder() {
      getSlides();

      slides
        .sort((a, b) => {
          const orderA =
            parseInt(a.dataset.originalOrder, 10) || 0;

          const orderB =
            parseInt(b.dataset.originalOrder, 10) || 0;

          return orderA - orderB;
        })
        .forEach((slide) => track.appendChild(slide));

      getSlides();
    }

    function applyMobileOrder() {
      const enabled =
        track.dataset.mobileOrderEnabled === 'true';

      if (!enabled || !mobile.matches) {
        restoreDesktopOrder();
        return;
      }

      getSlides();

      slides
        .sort((a, b) => {
          const mobileA =
            parseInt(a.dataset.mobileOrder, 10) || 10;

          const mobileB =
            parseInt(b.dataset.mobileOrder, 10) || 10;

          if (mobileA === mobileB) {
            const originalA =
              parseInt(a.dataset.originalOrder, 10) || 0;

            const originalB =
              parseInt(b.dataset.originalOrder, 10) || 0;

            return originalA - originalB;
          }

          return mobileA - mobileB;
        })
        .forEach((slide) => track.appendChild(slide));

      getSlides();
    }

    function buildDots() {
      dotsWrap.innerHTML = '';

      dots = slides.map((slide, index) => {
        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'drift-gallery-dot';
        button.setAttribute(
          'aria-label',
          `Go to slide ${index + 1}`
        );

        button.addEventListener('click', () => {
          goTo(index);
        });

        dotsWrap.appendChild(button);

        return button;
      });
    }

    function slideScrollLeft(slide) {
      /*
        Align each variable-width slide's left edge to the track viewport.
        This preserves the "peek" composition because the following slide
        remains visible whenever viewport space allows it.
      */
      return slide.offsetLeft;
    }

    function goTo(index, behavior = 'smooth') {
      if (!slides.length) return;

      const wrappedIndex =
        ((index % slides.length) + slides.length) % slides.length;

      activeIndex = wrappedIndex;

      track.scrollTo({
        left: slideScrollLeft(slides[wrappedIndex]),
        behavior
      });

      updateDots();
    }

    function nearestSlideIndex() {
      if (!slides.length) return 0;

      const scrollLeft = track.scrollLeft;

      let nearest = 0;
      let nearestDistance = Infinity;

      slides.forEach((slide, index) => {
        const distance =
          Math.abs(slideScrollLeft(slide) - scrollLeft);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = index;
        }
      });

      return nearest;
    }

    function updateDots() {
      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;

        dot.classList.toggle('is-active', isActive);

        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    function syncFromScroll() {
      scrollRaf = 0;

      const nextIndex = nearestSlideIndex();

      if (nextIndex !== activeIndex) {
        activeIndex = nextIndex;
        updateDots();
      }
    }

    function onScroll() {
      if (scrollRaf) return;

      scrollRaf =
        requestAnimationFrame(syncFromScroll);
    }

    function onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(activeIndex - 1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(activeIndex + 1);
      }
    }

    function rebuildForViewport() {
      applyMobileOrder();
      buildDots();

      activeIndex = Math.min(
        activeIndex,
        Math.max(0, slides.length - 1)
      );

      requestAnimationFrame(() => {
        goTo(activeIndex, 'auto');
      });
    }

    function onResize() {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);

      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = 0;

        const mobileChanged =
          lastMobileState !== mobile.matches;

        lastMobileState = mobile.matches;

        if (mobileChanged) {
          rebuildForViewport();
        } else {
          goTo(activeIndex, 'auto');
        }
      });
    }

    prevButton.addEventListener('click', () => {
      goTo(activeIndex - 1);
    });

    nextButton.addEventListener('click', () => {
      goTo(activeIndex + 1);
    });

    track.addEventListener('scroll', onScroll, {
      passive: true
    });

    track.addEventListener('keydown', onKeydown);

    window.addEventListener('resize', onResize);

    mobile.addEventListener?.(
      'change',
      rebuildForViewport
    );

    section.__driftGalleryCleanup = () => {
      track.removeEventListener('scroll', onScroll);
      track.removeEventListener('keydown', onKeydown);

      window.removeEventListener('resize', onResize);

      mobile.removeEventListener?.(
        'change',
        rebuildForViewport
      );

      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      if (resizeRaf) cancelAnimationFrame(resizeRaf);

      delete section.dataset.driftGalleryInitialized;
    };

    rebuildForViewport();
  }

  function initAll(root = document) {
    if (root.matches?.(SECTION_SELECTOR)) {
      initGallery(root);
    }

    root.querySelectorAll?.(SECTION_SELECTOR).forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAll(),
      { once: true }
    );
  } else {
    initAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => initAll(event.target)
  );

  document.addEventListener(
    'shopify:section:select',
    (event) => initAll(event.target)
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const section =
        event.target.matches?.(SECTION_SELECTOR)
          ? event.target
          : event.target.querySelector?.(SECTION_SELECTOR);

      if (section?.__driftGalleryCleanup) {
        section.__driftGalleryCleanup();
      }
    }
  );
})();
